import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaShieldAlt, FaChevronDown } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const sections = [
    {
        title: "Information We Collect",
        content: [
            {
                heading: "Personal Information",
                text: "When you register on DreamToday, we collect your full name, email address, phone number, and password. This information is necessary to create and manage your account.",
            },
            {
                heading: "Transaction Data",
                text: "We collect details of purchases, coin transactions, ticket bookings, and wallet activity to maintain accurate records and provide you with transaction history.",
            },
            {
                heading: "Device & Usage Data",
                text: "We automatically collect information about your device, browser, IP address, and how you interact with our platform to improve performance and detect fraud.",
            },
        ],
    },
    {
        title: "How We Use Your Information",
        content: [
            {
                heading: "Account Management",
                text: "Your personal information is used to create and maintain your account, verify your identity, and provide customer support.",
            },
            {
                heading: "Campaign & Ticket Services",
                text: "We use your data to process ticket purchases, conduct campaigns, notify winners, and coordinate prize delivery.",
            },
            {
                heading: "Communications",
                text: "We may send you transactional emails, OTPs, campaign announcements, and promotional messages. You can opt out of marketing communications at any time.",
            },
            {
                heading: "Platform Improvement",
                text: "Usage data helps us analyse trends, fix bugs, optimise performance, and develop new features to enhance your experience.",
            },
        ],
    },
    {
        title: "Data Sharing & Disclosure",
        content: [
            {
                heading: "We Never Sell Your Data",
                text: "DreamToday does not sell, rent, or trade your personal information to any third party for marketing purposes.",
            },
            {
                heading: "Service Providers",
                text: "We may share limited data with trusted third-party service providers (payment processors, SMS gateways, cloud hosting) solely to operate our platform. They are contractually obligated to protect your data.",
            },
            {
                heading: "Legal Requirements",
                text: "We may disclose your information if required by law, court order, or to protect the rights, safety, and property of DreamToday and its users.",
            },
        ],
    },
    {
        title: "Data Security",
        content: [
            {
                heading: "Encryption",
                text: "All data transmitted between your device and our servers is encrypted using industry-standard SSL/TLS protocols.",
            },
            {
                heading: "Password Protection",
                text: "Your password is stored using strong one-way hashing. We never store passwords in plain text.",
            },
            {
                heading: "Access Controls",
                text: "Access to your personal data is strictly limited to authorised personnel and is governed by role-based access controls.",
            },
        ],
    },
    {
        title: "Cookies & Tracking",
        content: [
            {
                heading: "Essential Cookies",
                text: "We use session cookies to keep you logged in and maintain your preferences during your visit.",
            },
            {
                heading: "Analytics",
                text: "We use anonymised analytics tools to understand how users navigate our platform. No personally identifiable information is shared with analytics providers.",
            },
            {
                heading: "Managing Cookies",
                text: "You can control or disable cookies through your browser settings. Note that disabling cookies may affect some functionality of the platform.",
            },
        ],
    },
    {
        title: "Your Rights",
        content: [
            {
                heading: "Access & Correction",
                text: "You have the right to access and update your personal information at any time through your account settings.",
            },
            {
                heading: "Data Deletion",
                text: "You may request deletion of your account and associated data by contacting our support team. Certain data may be retained for legal or regulatory purposes.",
            },
            {
                heading: "Portability",
                text: "You may request a copy of your personal data in a structured, machine-readable format.",
            },
        ],
    },
    {
        title: "Children's Privacy",
        content: [
            {
                heading: "Age Restriction",
                text: "DreamToday is strictly for users aged 18 and above. We do not knowingly collect personal information from minors. If we become aware that a minor has registered, we will immediately delete their account.",
            },
        ],
    },
    {
        title: "Changes to This Policy",
        content: [
            {
                heading: "Policy Updates",
                text: "We may update this Privacy Policy periodically. When we do, we will revise the 'Last Updated' date and notify registered users via email. Continued use of the platform after changes constitutes acceptance of the updated policy.",
            },
        ],
    },
];

const AccordionItem = ({ section, index, isOpen, onToggle }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        className={`rounded-2xl border overflow-hidden transition-all duration-300 ${isOpen ? "border-yellow-400 shadow-md" : "border-yellow-100"
            }`}
    >
        <button
            onClick={onToggle}
            className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left
        bg-white/80 hover:bg-yellow-50/60 transition"
        >
            <div className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500
          flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm">
                    {index + 1}
                </span>
                <span className={`font-semibold text-sm sm:text-base ${isOpen ? "text-yellow-700" : "text-gray-800"}`}>
                    {section.title}
                </span>
            </div>
            <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.25 }}>
                <FaChevronDown className={`text-sm ${isOpen ? "text-yellow-500" : "text-gray-400"}`} />
            </motion.div>
        </button>

        {isOpen && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="px-6 pb-6 pt-2 bg-gradient-to-b from-yellow-50/40 to-white/60 border-t border-yellow-100 space-y-4"
            >
                {section.content.map((item, i) => (
                    <div key={i}>
                        <p className="font-semibold text-gray-800 text-sm mb-1">{item.heading}</p>
                        <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
                    </div>
                ))}
            </motion.div>
        )}
    </motion.div>
);

const PrivacyPolicy = () => {
    const [openIndex, setOpenIndex] = useState(0);

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-gradient-to-b from-white via-[#FFF7DB] to-white pt-28 pb-20 px-4 relative overflow-hidden">

                {/* BG BLOBS */}
                <div className="absolute top-0 right-0 w-96 h-96 rounded-full
          bg-gradient-to-br from-yellow-100 to-amber-50 opacity-40 blur-3xl translate-x-1/3 -translate-y-1/4 pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full
          bg-gradient-to-br from-amber-100 to-yellow-50 opacity-30 blur-3xl -translate-x-1/3 translate-y-1/4 pointer-events-none" />

                <div className="max-w-4xl mx-auto relative z-10">

                    {/* HERO */}
                    <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-14">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500
              flex items-center justify-center shadow-lg mx-auto mb-5">
                            <FaShieldAlt className="text-white text-2xl" />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold mb-3
              bg-gradient-to-r from-yellow-600 via-yellow-700 to-yellow-800 bg-clip-text text-transparent">
                            Privacy Policy
                        </h1>
                        <p className="text-gray-500 text-sm">Last Updated: March 05, 2026</p>
                        <p className="text-gray-500 max-w-xl mx-auto mt-3 text-base">
                            Your privacy matters to us. This policy explains how DreamToday collects, uses, and protects your personal information.
                        </p>
                    </motion.div>

                    {/* HIGHLIGHT BANNER */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200
              rounded-2xl px-6 py-4 mb-10 flex items-start gap-4"
                    >
                        <FaShieldAlt className="text-yellow-500 text-xl shrink-0 mt-0.5" />
                        <p className="text-sm text-gray-600 leading-relaxed">
                            By using DreamToday, you agree to the collection and use of information as described in this policy.
                            We are committed to protecting your data and being transparent about our practices.
                        </p>
                    </motion.div>

                    {/* ACCORDION */}
                    <div className="space-y-3">
                        {sections.map((section, i) => (
                            <AccordionItem
                                key={i}
                                section={section}
                                index={i}
                                isOpen={openIndex === i}
                                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
                            />
                        ))}
                    </div>

                    {/* FOOTER NOTE */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="mt-14 text-center bg-gradient-to-r from-yellow-50 to-amber-50
              border border-yellow-200 rounded-3xl p-8"
                    >
                        <h3 className="text-lg font-bold text-gray-800 mb-2">Questions about your privacy?</h3>
                        <p className="text-gray-500 text-sm mb-5">
                            Contact our Data Protection team at{" "}
                            <a href="mailto:privacy@dreamtoday.com" className="text-yellow-600 font-medium hover:underline">
                                privacy@dreamtoday.com
                            </a>
                        </p>
                        <a href="/contactus"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white
                bg-gradient-to-r from-yellow-500 to-amber-600 shadow-md hover:scale-105 transition text-sm">
                            Contact Us
                        </a>
                    </motion.div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default PrivacyPolicy;